import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Cloud, Copy } from 'lucide-react';
import { saveContractToDropbox, saveInvoiceToDropbox, saveTerminationToDropbox } from '../api/dropbox.js';
import { useToast } from '../contexts/ToastContext';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { queryKeys } from '../lib/queryKeys';

/**
 * Shared "Save to Dropbox" button. Renders icon-only (cloud + optional copy-link).
 * Always mounted via DocumentActions.
 *
 * Role gate: any authenticated CRM role may save. Per-row scoping on the
 * backend (assertContractAccessible) still limits which entities a given
 * user can actually push.
 *
 * State machine:
 *   - dropboxUrl == null   →  ☁ save (push mode)
 *   - mutation pending     →  ☁ disabled while loading
 *   - dropboxUrl populated →  ☁ open in Dropbox (window.open new tab) + optional copy-link
 *   - disabled prop true   →  ☁ greyed, title=disabledReason
 */
export default function DropboxSaveButton ({
  entity,           // 'contract' | 'invoice'
  entityId,         // uuid
  dropboxUrl,       // null | string  — null = not yet saved
  sharedUrl = null, // null | string  — P8: real Dropbox share link; preferred for open + the «Копировать ссылку» action. null → graceful fallback to dropboxUrl (/home), no copy button.
  contractId,       // uuid | undefined  — only for entity='invoice', used to invalidate byContract key
  disabled = false, // contract not yet completed (contracts only)
  disabledReason,   // string | undefined  — title text when disabled (e.g. 'Договор ещё не подписан')
}) {
  const qc = useQueryClient();
  const { addToast } = useToast();
  const { user, isLoading } = useCurrentUser();

  const saveMutation = useMutation({
    mutationFn: () => entity === 'contract'
      ? saveContractToDropbox(entityId)
      : entity === 'termination'
        ? saveTerminationToDropbox(entityId)
        : saveInvoiceToDropbox(entityId),
    onSuccess: () => {
      addToast('Сохранено', 'Файл загружен в Dropbox', 'success');
      // TanStack v5 prefix-matches; broad keys are sufficient.
      if (entity === 'contract') {
        qc.invalidateQueries({ queryKey: ['contracts'] });
      } else if (entity === 'termination') {
        if (contractId) {
          qc.invalidateQueries({ queryKey: ['terminations', contractId] });
        }
      } else {
        if (contractId) {
          qc.invalidateQueries({ queryKey: queryKeys.invoices.byContract(contractId) });
        }
        qc.invalidateQueries({ queryKey: ['invoices'] });
      }
    },
    onError: (err) => {
      const status = err?.response?.status ?? err?.status;
      if (status === 503) {
        addToast('Не настроено', 'Dropbox не настроен — обратитесь к разработчику', 'warning');
      } else if (status === 409) {
        addToast('Не подписан', 'Договор ещё не подписан', 'warning');
      } else if (status === 403) {
        addToast('Нет прав', 'Этот документ вне вашей области видимости', 'error');
      } else {
        addToast('Ошибка', 'Не удалось сохранить в Dropbox, попробуйте ещё раз', 'error');
      }
    },
  });

  // Authenticated CRM roles can push; unknown roles get nothing. Per-row
  // visibility is enforced by the backend (assertContractAccessible) — this
  // gate is intentionally permissive.
  // (Role-check after hooks to satisfy React's rules-of-hooks.)
  if (isLoading) return null;
  if (!user) return null;

  // P8: open the real share link when we have one; gracefully fall back to the
  // /home browser URL (CONTEXT D-15) for docs pushed before sharing was enabled.
  const openUrl = sharedUrl || dropboxUrl;

  // P8: «Копировать ссылку» — copy the public share link to the clipboard.
  // Only offered when a real sharedUrl exists (the /home link is not shareable).
  const copyShareLink = async () => {
    if (!sharedUrl) return;
    try {
      await navigator.clipboard.writeText(sharedUrl);
      addToast('Скопировано', 'Ссылка на Dropbox скопирована', 'success');
    } catch {
      addToast('Ошибка', 'Не удалось скопировать ссылку', 'error');
    }
  };

  if (dropboxUrl) {
    return (
      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <button
          type="button"
          className="board-action-btn dropbox-save-btn dropbox-save-btn--saved"
          onClick={() => window.open(openUrl, '_blank', 'noopener,noreferrer')}
          title="Открыть в Dropbox"
          aria-label="Открыть в Dropbox"
          style={{ padding: '4px 8px' }}
        >
          <Cloud size={14} style={{ color: '#10b981' }} />
        </button>
        {sharedUrl && (
          <button
            type="button"
            className="board-action-btn dropbox-copy-btn"
            onClick={copyShareLink}
            title="Копировать ссылку на Dropbox"
            aria-label="Копировать ссылку на Dropbox"
            style={{ padding: '4px 8px' }}
          >
            <Copy size={14} />
          </button>
        )}
      </span>
    );
  }
  if (disabled) {
    return (
      <button
        type="button"
        className="board-action-btn dropbox-save-btn"
        disabled
        title={disabledReason || 'Недоступно'}
        aria-label={disabledReason || 'Сохранить в Dropbox'}
        style={{ padding: '4px 8px', opacity: 0.5 }}
      >
        <Cloud size={14} />
      </button>
    );
  }

  return (
    <button
      type="button"
      className="board-action-btn dropbox-save-btn"
      disabled={saveMutation.isPending}
      title={saveMutation.isPending ? 'Загрузка…' : 'Сохранить в Dropbox'}
      aria-label="Сохранить в Dropbox"
      onClick={() => saveMutation.mutate()}
      style={{ padding: '4px 8px' }}
    >
      <Cloud size={14} />
    </button>
  );
}
